import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ContactForm.css"; // Make sure to have a CSS file for styles
import sendEmail from "./EmailSender";
import { ThreeDots } from "react-loader-spinner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css"; // Import the necessary styles for PhoneInput

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "Other",
    phoneNumber: "",
    message: "",
    organizationName: "",
    role: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const handlePhoneChange = (value) => {
    const phoneValue = value ? String(value) : "";
    setFormData((prevState) => ({ ...prevState, phoneNumber: phoneValue }));
    const valid = isValidPhoneNumber(phoneValue);
    console.log("is valid", valid);
    setIsPhoneValid(valid); // Update phone validity state
    // setIsPhoneValid(true); // Clear phone error when the user starts typing
  };

  // Clear the success message after 1 minute
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(""); // Clear the success message after 1 minute
      }, 10000); // 1 minute = 60000 milliseconds

      // Cleanup the timer when the component is unmounted or when success changes
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    setError(""); // Clear error message when the user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Regular expressions for validating email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check if all fields have a value and validate email and phone number
    if (
      !formData.name ||
      !formData.email ||
      !formData.phoneNumber ||
      !formData.message ||
      !formData.organizationName ||
      !formData.role ||
      formData.category === "Select One"
    ) {
      setError("All fields are required. Please check and try again.");
      return;
    }

    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Check if phone number is valid
    if (!isPhoneValid) {
      setError("Please enter a valid phone number.");
      return;
    }

    setError("");
    setLoading(true);

    // Prepare the data for the API
    const requestData = {
      inquirer_name: formData.name,
      email: formData.email,
      inquiry_about: formData.category,
      phone_number: formData.phoneNumber,
      message: formData.message,
      organization_name: formData.organizationName,
      role_in_organization: formData.role,
    };

    try {
      const response = await axios.post(
        "https://app-acs-dev-eastus.azurewebsites.net/add_inquiry",
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        // if (supportEmailSent) {
        const currentDate = new Date();
        const currentDay = currentDate.getDay();
        let message =
          "Thank you for your inquiry. Our team will contact you within 24 hours.";
        if (currentDay === 6 || currentDay === 0) {
          message =
            "Thank you for your inquiry. Our team will contact you on the next business day.";
        }

        setSuccess(message);
        setLoading(false);

        // } else {
        //   setError(
        //     "There was a problem sending confirmation emails. Please try again later."
        //   );
        // }
        // Send email to support team
        const supportEmailSent = await sendEmail({
          application_name: "Inquiry Form",
          env_type: "Production",
          subject: "New Inquiry Received",
          message: `Dear Support Team,

We have received a new inquiry with the following details:

Name: ${formData.name}
Email: ${formData.email}
Inquiry Category: ${formData.category}
Phone Number: ${formData.phoneNumber}
Message: ${formData.message}
Organization Name: ${formData.organizationName}
Role in Organization: ${formData.role}

Please follow up with the inquirer as soon as possible.

Best regards,
SST Support Team`,
          receiver_addresses: ["sstofficial@sstglobal.net"],
        });
        setFormData({
          name: "",
          email: "",
          category: "Other",
          phoneNumber: "",
          message: "",
          organizationName: "",
          role: "",
        });
      } else {
        setError(
          "There was a problem submitting your inquiry. Please try again later."
        );
      }
    } catch (error) {
      setError(
        "There was a problem submitting your inquiry. Please try again later."
      );
    }
  };

  return (
    <div className="contact-form-container">
      <h2 className="contact-form-header">GET IN TOUCH</h2>
      <div className="contact-form-card">
        {success && <div className="success-message">{success}</div>}
        <form className="contact-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="form-input"
            id="inputName"
            name="name"
            placeholder="Your Name:"
            value={formData.name}
            onChange={handleChange}
          />
          <input
            type="email"
            className="form-input"
            id="inputEmail"
            name="email"
            placeholder="Your Email:"
            value={formData.email}
            onChange={handleChange}
          />
          <select
            id="inputState"
            className="form-select"
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            <option value="Other">Other</option>
            <option value="LLMInsight">LLMInsight</option>
            <option value="GenAI">GenAI Development</option>
            <option value="Web Development">Web Development</option>
            <option value="Mobile Development">Mobile Development</option>
            <option value="Manage Existing Cloud">Manage Existing Cloud</option>
            <option value="Migrate to the Cloud">Migrate to the Cloud</option>
            <option value="Consulting Advisory">Consulting Advisory</option>
            <option value="Security Assessment & Compliance Related">
              Security Assessment & Compliance Related
            </option>
            <option value="Healthcare">Healthcare</option>
            <option value="Insurance">Insurance</option>
            <option value="Utility">Utility</option>
            <option value="E-commerce">E-commerce</option>
          </select>

          {/* Phone Input with country code */}
          <PhoneInput
            country="us" // Default country is USA
            id="inputPhone"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handlePhoneChange} // Handle phone input change
            // preferredCountries={["us", "in", "uk"]} // Preferred countries
            placeholder="Enter phone number"
            className="form-input"
            required
            isValid={(valid) => setIsPhoneValid(valid)} // Use the built-in validation
          />
          <input
            type="text"
            className="form-input"
            id="inputOrganizationName"
            name="organizationName"
            placeholder="Your Organization Name:"
            value={formData.organizationName}
            onChange={handleChange}
          />
          <input
            type="text"
            className="form-input"
            id="inputRole"
            name="role"
            placeholder="Your Role in Organization:"
            value={formData.role}
            onChange={handleChange}
          />
          <textarea
            className="form-input"
            id="inputMessage"
            name="message"
            placeholder="Your Message:"
            value={formData.message}
            onChange={handleChange}
            rows="4"
          ></textarea>
           <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (
              <ThreeDots
                height="20"
                width="40"
                color="#3498db" // Set the spinner color
                ariaLabel="three-dots-loading"
                visible={true}
              />
            ) : (
              "SUBMIT NOW"
            )}
          </button>
          {error && <div className="error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default ContactForm;